import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useBeforeUnload, useParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { Save, Bot, SidebarClose, SidebarOpen, Cuboid, Sliders } from 'lucide-react';

import FlowCanvas from '../components/FlowCanvas';
import ChatPanel from '../components/ChatPanel';
import NodesPanel from '../components/NodesPanel';
import ParamsPanel from '../components/ParamsPanel';
import { getWorkflow, updateWorkflow } from '../api/workflows';
import { listNodeDefs } from '../api/nodes';
import { getChatHistory, sendChatSse } from '../api/chat';
import { adaptDagToReactFlow, toBackendDag } from '../utils/graphAdapters';
import { useFlowDirty } from '../contexts/FlowDirtyContext';

const DEFAULT_LAYER = 'offline';

export default function DagStudioPage() {
  const { workflowId } = useParams();
  const { setWorkflowDirty, clearWorkflowDirty } = useFlowDirty();
  
  // Graph State
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [nodeDefs, setNodeDefs] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  // Chat State
  const [chatMessages, setChatMessages] = useState([]);
  const [chatStreamEvents, setChatStreamEvents] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('chat');
  const [isChatStreaming, setIsChatStreaming] = useState(false);
  const chatAbortRef = useRef(null);

  const flowCanvasElRef = useRef(null);
  const reactFlowInstanceRef = useRef(null);

  const selectedNodes = useMemo(() => nodes.filter((n) => n.selected), [nodes]);
  const prevSelectedCountRef = useRef(0);

  useEffect(() => {
    const count = selectedNodes.length;
    const prev = prevSelectedCountRef.current;

    if (prev === 0 && count > 0) {
      setActiveTab('params');
    }
    else if (prev > 0 && count === 0) {
      if (activeTab === 'params') {
        setActiveTab('chat');
      }
    }

    prevSelectedCountRef.current = count;
  }, [selectedNodes.length, activeTab]);

  // UI State
  const [isSaving, setIsSaving] = useState(false);

  const setNodesDirty = useCallback(
    (updater) => {
      setIsDirty(true);
      setNodes(updater);
    },
    [setNodes],
  );

  const setEdgesDirty = useCallback(
    (updater) => {
      setIsDirty(true);
      setEdges(updater);
    },
    [setEdges],
  );

  const createNode = useCallback(
    (nodeType, position) => {
      if (!nodeType) return

      const id = crypto?.randomUUID ? crypto.randomUUID() : `n_${Date.now()}_${Math.random().toString(16).slice(2)}`
      const def = (nodeDefs || []).find((d) => d?.name === nodeType)
      const label = def?.name || nodeType
      const description = def?.description || ''

      const finalPos = position && typeof position.x === 'number' && typeof position.y === 'number'
        ? position
        : { x: 80 + Math.random() * 240, y: 80 + Math.random() * 240 }

      setNodesDirty((prev) => [
        ...(prev || []),
        {
          id,
          type: 'default',
          selected: false,
          position: finalPos,
          data: {
            label,
            subtitle: description,
            type: nodeType,
            output_name: null,
            params: {},
          },
        },
      ])
    },
    [nodeDefs, setNodesDirty],
  )

  const addNodeAtCanvasCenter = useCallback(
    (nodeType) => {
      const inst = reactFlowInstanceRef.current
      const el = flowCanvasElRef.current
      if (!inst || !el) {
        createNode(nodeType)
        return
      }

      const rect = el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      const vp = inst.getViewport?.() || { x: 0, y: 0, zoom: 1 }
      const zoom = vp.zoom || 1

      const position = {
        x: (cx - (vp.x || 0)) / zoom,
        y: (cy - (vp.y || 0)) / zoom,
      }

      createNode(nodeType, position)
    },
    [createNode],
  )

  useBeforeUnload(
    (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    },
    { capture: true },
  );

  useLayoutEffect(() => {
    setWorkflowDirty(workflowId, false);
  }, [workflowId, setWorkflowDirty]);

  useEffect(() => {
    setWorkflowDirty(workflowId, isDirty);
    return () => {
      clearWorkflowDirty(workflowId);
    };
  }, [workflowId, isDirty, setWorkflowDirty, clearWorkflowDirty]);



  // Load Data
  useEffect(() => {
    async function loadData() {
      if (!workflowId) return;
      setLoading(true);
      try {
        const [wf, defs] = await Promise.all([
            getWorkflow(workflowId),
            listNodeDefs()
        ]);
        
        setWorkflow(wf);
        const adapted = adaptDagToReactFlow(wf?.graph_data ?? {}, { preservePositionsFrom: [] })
        setNodes((adapted?.nodes ?? []).map((n) => ({ ...n, selected: false })));
        setEdges((adapted?.edges ?? []).map((e) => ({ ...e, selected: false })));
        setIsDirty(false);
        setNodeDefs(defs);
        
        // Load chat history
        const history = await getChatHistory(workflowId);
        setChatMessages(history);
      } catch (err) {
        console.error("Failed to load workflow data", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [workflowId]);

  // Actions
  async function handleSave() {
    setIsSaving(true);
    try {
      const positionsById = {}
      for (const n of nodes || []) {
        if (!n?.id) continue
        const x = n?.position?.x
        const y = n?.position?.y
        if (typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)) {
          positionsById[String(n.id)] = { x, y }
        }
      }

      const viewport = reactFlowInstanceRef.current?.getViewport?.() || null
      const backendDag = toBackendDag({ nodes, edges })

      const updatedWf = await updateWorkflow(workflowId, {
        graph_data: {
          version: 12,
          nodes: backendDag.nodes,
          edges: backendDag.edges,
          ui: {
            positionsById,
            viewport,
          },
        },
      });
      setWorkflow(updatedWf);
      setIsDirty(false);
    } catch (err) {
      console.error("Failed to save", err);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSendChat({ message, mode = 'auto', model }) {
    if (!workflowId) return;

    if (chatAbortRef.current) {
      chatAbortRef.current.abort();
    }

    setChatStreamEvents([]);
    const abort = new AbortController();
    chatAbortRef.current = abort;
    setIsChatStreaming(true);

    setChatMessages((prev) => [
      ...prev,
      { role: 'user', content: message, source: 'user', created_at: new Date().toISOString() },
    ]);

    try {
        await sendChatSse(
          {
            session_id: workflowId,
            message,
            layer: DEFAULT_LAYER,
            mode,
            model,
            client_context: {
              current_graph_dag: toBackendDag({ nodes, edges }),
              last_graph_dag: toBackendDag({ nodes, edges }),
            },
          },
          {
            signal: abort.signal,
            onEvent: (evt) => {
              setChatStreamEvents((prev) => [...prev, evt]);
    
              if (evt.type === 'message') {
                setChatMessages((prev) => [
                  ...prev,
                  {
                    role: 'assistant',
                    content: evt.content,
                    source: evt.source || 'assistant',
                    created_at: new Date().toISOString(),
                  },
                ]);
              }
    
              if (evt.type === 'graph_generated' && evt.dag) {
                const adapted = adaptDagToReactFlow(evt.dag, { preservePositionsFrom: nodes });
                setNodesDirty((adapted.nodes || []).map((n) => ({ ...n, selected: false })));
                setEdgesDirty((adapted.edges || []).map((e) => ({ ...e, selected: false })));
                // Optionally auto-save here
              }
            },
          },
        );
    } catch (e) {
        if (e.name !== 'AbortError') {
             console.error("Chat error", e);
        }
    } finally {
        chatAbortRef.current = null;
        setIsChatStreaming(false);
        // Refresh history to ensure sync
        const history = await getChatHistory(workflowId);
        setChatMessages(history);
    }
  }

  if (loading) {
     return <div className="flex items-center justify-center h-full text-[var(--text-secondary)]">Loading Studio...</div>;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Top Bar */}
      <header className="h-14 border-b border-[var(--border-subtle)] bg-[var(--bg-panel)] flex items-center justify-between px-4">
        <div className="flex items-center">
            <h2 className="font-semibold text-sm mr-4">{workflow?.name || 'Untitled Strategy'}</h2>
            <span className="badge badge-blue">v{workflow?.version}</span>
            {isDirty ? <span className="ml-3 text-xs text-[var(--accent-red)]">Unsaved</span> : null}
        </div>
        <div className="flex items-center gap-2">
            <button 
                onClick={handleSave} 
                disabled={isSaving || !isDirty}
                className="btn btn-primary"
            >
                <Save size={16} />
                {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button 
                onClick={() => setIsChatOpen(!isChatOpen)}
                className={`btn btn-icon ${isChatOpen ? 'bg-[var(--accent-blue-dim)] text-[var(--accent-blue)]' : ''}`}
            >
                {isChatOpen ? <SidebarClose size={18} /> : <SidebarOpen size={18} />}
            </button>
        </div>
      </header>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        <div className="flex-1 relative bg-[var(--bg-app)]">
             <ReactFlowProvider>
                 <FlowCanvas 
                     nodes={nodes} 
                     edges={edges} 
                     setNodes={setNodesDirty} 
                     setEdges={setEdgesDirty} 
                     nodeDefs={nodeDefs}
                     canvasElRef={flowCanvasElRef}
                     onInit={(inst) => {
                       reactFlowInstanceRef.current = inst
                     }}
                     onCreateNode={createNode}
                 />
              </ReactFlowProvider>
        </div>

        {/* Chat Sidebar */}
        {isChatOpen && (
            <div className="w-[360px] border-l border-[var(--border-subtle)] bg-[var(--bg-panel)] flex flex-col transition-all duration-300">
                <div className="flex items-center p-1 mx-3 mt-3 mb-2 bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded-lg" role="tablist" aria-label="Sidebar Tabs">
                    <button 
                        role="tab" 
                        aria-selected={activeTab === 'chat'} 
                        onClick={() => setActiveTab('chat')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-all duration-200 rounded-md ${activeTab === 'chat' ? 'text-[var(--accent-purple)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-panel-hover)]'}`}
                        style={activeTab === 'chat' ? { backgroundColor: 'rgba(163, 113, 247, 0.15)' } : {}}
                    >
                        <Bot size={16} className={activeTab === 'chat' ? "text-[var(--accent-purple)]" : "opacity-70"}/>
                        AI Assistant
                    </button>
                    <button 
                        role="tab" 
                        aria-selected={activeTab === 'nodes'} 
                        onClick={() => setActiveTab('nodes')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-all duration-200 rounded-md ${activeTab === 'nodes' ? 'text-[var(--accent-blue)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-panel-hover)]'}`}
                        style={activeTab === 'nodes' ? { backgroundColor: 'rgba(47, 129, 247, 0.22)' } : {}}
                    >
                        <Cuboid size={16} className={activeTab === 'nodes' ? "text-[var(--accent-blue)]" : "opacity-70"}/>
                         Nodes
                    </button>
                    {selectedNodes.length > 0 && (
                      <button 
                          role="tab" 
                          aria-selected={activeTab === 'params'} 
                          onClick={() => setActiveTab('params')}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-all duration-200 rounded-md ${activeTab === 'params' ? 'text-[var(--accent-green)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-panel-hover)]'}`}
                          style={activeTab === 'params' ? { backgroundColor: 'rgba(46, 160, 67, 0.15)' } : {}}
                      >
                          <Sliders size={16} className={activeTab === 'params' ? "text-[var(--accent-green)]" : "opacity-70"}/>
                          Params
                      </button>
                    )}
                </div>

                <div className="flex-1 overflow-hidden">
                     <div className={activeTab === 'chat' ? 'h-full block' : 'hidden h-full'}>
                        <ChatPanel 
                            disabled={isChatStreaming}
                            messages={chatMessages}
                            streamEvents={chatStreamEvents}
                            onSend={handleSendChat}
                            onStop={() => {
                            chatAbortRef.current?.abort();
                            setIsChatStreaming(false);
                            }}
                        />
                     </div>
                     <div className={activeTab === 'nodes' ? 'h-full block' : 'hidden h-full'}>
                        <NodesPanel nodeDefs={nodeDefs} onPickNode={addNodeAtCanvasCenter} />
                     </div>
                     {activeTab === 'params' && (
                        <div className="h-full block">
                            <ParamsPanel 
                                selectedNodes={selectedNodes}
                                nodeDefs={nodeDefs}
                                setNodesDirty={setNodesDirty}
                            />
                        </div>
                     )}
                 </div>
             </div>
         )}
      </div>
    </div>
  );
}
